import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { apiMessage, extractData, scanByCode, scanErrorPayload } from 'shared/api/scanner'
import { useEvent } from 'context/EventContext'
import Button from 'shared/components/ui/button'
import { IconBack, IconTicket } from 'shared/components/icons'
import { cn } from 'shared/util/cn'
import { displayTicketCode } from 'shared/util/ticketCode'
import loginHero from '../assets/login-hero.svg'

const STATUS_UI = {
  ready: {
    label: 'Confirm admit',
    kicker: 'Ticket scanned',
    subtitle: 'Choose how many guests to let in. Nothing is admitted until you confirm.',
    className: 'bg-[#3B1818] text-light'
  },
  admitted: {
    label: 'Admitted',
    kicker: 'Scan result',
    subtitle: 'Guests are in. Scan the next ticket when you are ready.',
    className: 'bg-success text-light'
  },
  invalid: {
    label: 'Invalid ticket',
    kicker: 'Scan result',
    subtitle: 'This code is not valid for this event.',
    className: 'bg-danger text-light'
  },
  used: {
    label: 'Already used',
    kicker: 'Scan result',
    subtitle: 'This ticket has already been admitted.',
    className: 'bg-primary text-dark'
  },
  expired: {
    label: 'Ticket expired',
    kicker: 'Scan result',
    subtitle: 'This ticket is no longer valid.',
    className: 'bg-theme-400 text-light'
  },
  over: {
    label: 'Not enough remaining',
    kicker: 'Scan result',
    subtitle: 'You asked for more guests than this booking has left.',
    className: 'bg-theme text-light'
  }
}

function isOverAdmitError(httpStatus, message, payload) {
  const msg = String(message || payload?.sMessage || '').toLowerCase()
  if (msg.includes('inefficient') || msg.includes('insufficient') || (msg.includes('remaining') && msg.includes('request'))) {
    return true
  }
  if (httpStatus === 422 && (payload?.nRequested != null || payload?.nRemaining != null)) return true
  return false
}

function classifyResult(payload, httpStatus, message) {
  const msg = String(message || payload?.sMessage || '').toLowerCase()
  if (isOverAdmitError(httpStatus, message, payload)) return 'over'
  if (msg.includes('invalid') || msg.includes('not found') || msg.includes('unknown')) return 'invalid'
  if (msg.includes('duplicate') || msg.includes('already')) return 'used'
  if (msg.includes('expired') || msg.includes('ticket_expired')) {
    if (payload?.nAdmitted > 0 && Number(payload?.nRemaining) === 0) return 'used'
    return 'expired'
  }
  if (payload && (Number(payload.nAdmittedNow) > 0 || Number(payload.nAdmitted) > 0)) return 'admitted'
  if (payload && Number(payload.nRemaining) === 0 && Number(payload.nAdmitted) > 0) return 'used'
  if (httpStatus && httpStatus >= 400) return 'invalid'
  return 'admitted'
}

function holderName(ticket) {
  const holder = ticket?.oHolder || {}
  const user = ticket?.oOrderUser || {}
  return (
    holder.sName ||
    [holder.sFirstName, holder.sLastName].filter(Boolean).join(' ') ||
    [user.sFirstName, user.sLastName].filter(Boolean).join(' ') ||
    'Guest'
  )
}

function remainingOf(payload) {
  for (const key of ['nRemaining', 'nRemainingTickets']) {
    const value = payload?.[key]
    if (value == null || value === '') continue
    const num = Number(value)
    if (Number.isFinite(num)) return num
  }
  const total = Number(payload?.nAdmit)
  const admitted = Number(payload?.nAdmitted)
  if (Number.isFinite(total) && total >= 0) {
    if (Number.isFinite(admitted)) return Math.max(0, total - admitted)
    return total
  }
  return null
}

function bookedTotalOf(payload) {
  const total = Number(payload?.nAdmit)
  if (Number.isFinite(total) && total > 0) return total
  const remaining = remainingOf(payload)
  const admitted = Number(payload?.nAdmitted)
  if (remaining != null && Number.isFinite(admitted)) return remaining + admitted
  return remaining
}

function applyTicketPayload(payload, setter) {
  if (payload && (payload.oHolder || payload.oPlan || payload.nAdmit != null || payload.nRemaining != null)) {
    setter(payload)
  }
}

function GuestStepper({ value, min = 1, max = 10, onChange }) {
  return (
    <div className="flex items-center justify-center gap-5">
      <button
        type="button"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-elevated text-3xl text-ink shadow-sm disabled:cursor-not-allowed disabled:opacity-35"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="Decrease guest count"
      >
        −
      </button>
      <div className="flex min-w-[4.5rem] flex-col items-center">
        <span className="text-6xl font-extrabold leading-none text-ink">{value}</span>
        <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          {value === 1 ? 'guest' : 'guests'}
        </span>
      </div>
      <button
        type="button"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-theme text-3xl text-light shadow-glow disabled:cursor-not-allowed disabled:bg-dark-600 disabled:opacity-40 disabled:shadow-none"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label="Increase guest count"
      >
        +
      </button>
    </div>
  )
}

GuestStepper.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired
}

function TicketStub({ code }) {
  return (
    <div className="relative mb-5 overflow-hidden rounded-[28px] bg-surface shadow-card">
      <div className="flex items-center gap-3 px-5 pt-5 pb-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-theme-900 text-theme">
          <IconTicket className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted">Ticket code</p>
          <p className="mb-0 font-mono text-4xl font-bold tracking-[0.22em] text-ink">{code}</p>
        </div>
      </div>
      <div className="relative mx-3 border-t border-dashed border-line">
        <span className="absolute -left-3 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-bg" />
        <span className="absolute -right-3 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-bg" />
      </div>
      <p className="mb-0 px-5 py-3 text-center text-xs text-muted">Ready to admit · nothing is scanned yet</p>
    </div>
  )
}

TicketStub.propTypes = { code: PropTypes.string.isRequired }

function HolderCard({ ticket, admitted, total, remainingCount, seats, showProgress }) {
  return (
    <div className="card-surface mb-5 space-y-3 p-5">
      <div>
        <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">Ticket holder</p>
        <p className="mb-0 text-4xl font-bold leading-tight">{holderName(ticket)}</p>
      </div>
      <p className="mb-0 text-sm text-muted">
        {ticket?.oHolder?.sEmail || ticket?.oOrderUser?.sEmail || ticket?.oHolder?.sMobile || '—'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-theme-900 px-2.5 py-1 text-xs font-semibold text-theme">
          {ticket?.oPlan?.sTitle || 'Ticket'}
        </span>
        {seats.length > 0 && (
          <span className="rounded-full bg-elevated px-2.5 py-1 text-xs font-semibold text-ink">
            Seats {seats.join(', ')}
          </span>
        )}
      </div>
      {showProgress && (
        <div className="pt-1">
          <div className="mb-2 flex items-center justify-between text-xs text-muted">
            <span>
              {admitted} of {total || remainingCount + admitted} in
            </span>
            <span className="font-semibold text-ink">{remainingCount} remaining</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-line">
            <div
              className="h-full rounded-full bg-success"
              style={{
                width: `${(total || remainingCount + admitted) ? Math.min(100, (admitted / (total || remainingCount + admitted)) * 100) : 0}%`
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

HolderCard.propTypes = {
  ticket: PropTypes.object.isRequired,
  admitted: PropTypes.number,
  total: PropTypes.number,
  remainingCount: PropTypes.number,
  seats: PropTypes.array,
  showProgress: PropTypes.bool
}

function ScanResult() {
  const { eventId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { lastScan, setLastScan, addScanHistory } = useEvent()
  const code = location.state?.code || lastScan?.code
  const [ticket, setTicket] = useState(null)
  const [status, setStatus] = useState(code ? 'ready' : 'invalid')
  const [errorText, setErrorText] = useState(code ? '' : 'No ticket code to admit.')
  const [remainingHint, setRemainingHint] = useState(null)
  const [admitCount, setAdmitCount] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const requestIdRef = useRef(0)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!code) {
      setStatus('invalid')
      setErrorText('No ticket code to admit.')
    }
  }, [code])

  const runScan = useCallback(
    async (count, maxAllowed) => {
      if (!code || inFlightRef.current) return
      const requestId = ++requestIdRef.current
      const safeCount = Math.max(1, Number(count) || 1)
      const payloadCount = maxAllowed > 0 ? Math.min(safeCount, maxAllowed) : safeCount
      inFlightRef.current = true
      setSubmitting(true)
      setErrorText('')
      try {
        const res = await scanByCode(eventId, code, payloadCount)
        if (requestId !== requestIdRef.current) return
        const payload = extractData(res)
        const nextStatus = classifyResult(payload, res.status, res.data?.sMessage)
        setTicket(payload)
        setStatus(nextStatus)
        setRemainingHint(null)
        setAdmitCount(1)
        setLastScan({ status: nextStatus, code, ticket: payload })
        addScanHistory({
          code: displayTicketCode(code),
          status: nextStatus,
          name: holderName(payload),
          at: Date.now()
        })
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        const payload = scanErrorPayload(err)
        const msg = apiMessage(err)
        const nextStatus = classifyResult(payload, err?.response?.status, msg)
        applyTicketPayload(payload, setTicket)
        setStatus(nextStatus)
        setErrorText(msg)
        if (nextStatus === 'over') {
          const remaining = remainingOf(payload)
          setRemainingHint({
            remaining,
            requested: Number(payload?.nRequested) || payloadCount
          })
          if (remaining != null && remaining >= 1) {
            setAdmitCount(remaining)
            setStatus('ready')
          }
        } else {
          addScanHistory({
            code: displayTicketCode(code),
            status: nextStatus,
            name: holderName(payload),
            at: Date.now()
          })
        }
      } finally {
        if (requestId === requestIdRef.current) {
          inFlightRef.current = false
          setSubmitting(false)
        }
      }
    },
    [addScanHistory, code, eventId, setLastScan]
  )

  const remaining = remainingOf(ticket) ?? remainingHint?.remaining
  const remainingKnown = remaining != null
  const remainingCount = remainingKnown ? remaining : 0
  const total = bookedTotalOf(ticket) || 0
  const admitted = ticket?.nAdmitted || 0
  const isGroup = ticket?.bIsGroupTicket
  const seats = ticket?.aSeatLabels || []
  const ui = STATUS_UI[status] || STATUS_UI.invalid
  const stepperMax = remainingKnown ? Math.max(0, remainingCount) : (total > 0 ? total : 10)
  const canAdmit =
    stepperMax > 0 &&
    (status === 'ready' || status === 'over' || (status === 'admitted' && isGroup !== false && remainingCount > 0))
  const nextCount = stepperMax > 0 ? Math.min(Math.max(1, admitCount), stepperMax) : 1
  const showOverHint =
    remainingHint?.requested != null && remainingHint.remaining != null && remainingHint.requested > remainingHint.remaining

  const scanNext = () => navigate(`/events/${eventId}/scan`, { replace: true })
  const goHome = () => navigate(`/events/${eventId}`)

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <div className="flex items-center justify-between px-4 pt-4">
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-2xl bg-surface shadow-sm"
          onClick={status === 'ready' ? scanNext : goHome}
          aria-label="Back"
        >
          <IconBack className="h-5 w-5" />
        </button>
        <p className="mb-0 text-sm font-semibold text-ink">Gate check-in</p>
        <span className="w-11" />
      </div>

      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-4 pb-6 pt-3">
        <div className={cn('relative mb-5 overflow-hidden rounded-[28px] px-5 py-6 shadow-card', ui.className)}>
          {status === 'ready' && (
            <>
              <img src={loginHero} alt="" className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 opacity-[0.18]" />
              <div className="pointer-events-none absolute -bottom-8 -left-6 h-20 w-20 rounded-full bg-theme/30 blur-2xl" />
            </>
          )}
          <p className="relative mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] opacity-80">{ui.kicker}</p>
          <h1 className="relative mb-1 text-5xl font-extrabold leading-none">{ui.label}</h1>
          <p className="relative mb-0 max-w-[20rem] text-sm opacity-80">{ui.subtitle}</p>
        </div>

        {showOverHint && (
          <div className="mb-4 rounded-2xl border border-theme bg-theme-900 px-4 py-3 text-sm font-medium text-theme">
            Only {remainingHint.remaining} remaining — you requested {remainingHint.requested}
          </div>
        )}
        {errorText && status !== 'admitted' && status !== 'over' && status !== 'ready' && (
          <p className="mb-4 text-center text-sm text-muted">{errorText}</p>
        )}

        {ticket
          ? (
          <HolderCard
            ticket={ticket}
            admitted={admitted}
            total={total}
            remainingCount={remainingCount}
            seats={seats}
            showProgress={Boolean(isGroup || total > 0 || remainingKnown)}
          />
            )
          : code
            ? (
          <TicketStub code={displayTicketCode(code)} />
              )
            : null}

        {canAdmit && (
          <div className="mb-5 rounded-[28px] bg-surface p-5 shadow-card">
            <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
              {status === 'admitted' ? 'Admit more from this QR' : 'Guests entering'}
            </p>
            <p className="mb-5 text-center text-sm font-medium text-ink">
              {status === 'admitted' ? 'How many more to let in?' : 'How many guests are entering?'}
            </p>
            <GuestStepper value={nextCount} min={1} max={stepperMax} onChange={setAdmitCount} />
            {remainingKnown && (
              <p className="mb-0 mt-3 text-center text-xs text-muted">
                Max {stepperMax} {stepperMax === 1 ? 'guest' : 'guests'} on this ticket
              </p>
            )}
            <Button
              className="mt-5 w-full min-h-14 text-xl"
              onClick={() => runScan(nextCount, stepperMax)}
              disabled={submitting}
            >
              {submitting ? 'Admitting...' : `Admit ${nextCount} ${nextCount === 1 ? 'guest' : 'guests'}`}
            </Button>
          </div>
        )}

        <div className={cn('pt-1', canAdmit && 'mt-auto')}>
          <Button className="w-full" variant={canAdmit ? 'ghost' : 'primary'} onClick={scanNext} disabled={submitting}>
            {status === 'ready' ? 'Scan another ticket' : 'Scan next'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ScanResult
